import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";

/*
 * Wholesale price management – port of wholesale_prices.php (API: backend/inventory.py)
 * Enter the total package price per product. Per bottle = package price ÷ capacity;
 * profit per bottle = per-bottle price − purchase price; margin = profit ÷ per-bottle price.
 * Saving sends every cell: an empty or 0 cell removes that product from that package.
 */

const typeOf = (name) => {
  const n = (name || "").toLowerCase();
  return n.includes("maji") ? "maji" : n.includes("soda") ? "soda" : "other";
};
const FILTERS = [["all", "All"], ["soda", "Soda"], ["maji", "Maji"], ["other", "Other"], ["noprice", "No price"]];

export default function WholesalePrices() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState({}); // grid[productId][packageId] = string
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/api/wholesale/prices");
      setData(d);
      const g = {};
      d.products.forEach((p) => {
        g[p.id] = {};
        d.packages.forEach((k) => {
          const v = d.existing[p.id]?.[k.id];
          g[p.id][k.id] = v > 0 ? v.toFixed(2) : "";
        });
      });
      setGrid(g);
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const hasPrice = useCallback((pid) => Object.values(grid[pid] || {}).some((v) => parseFloat(v) > 0), [grid]);
  const counts = useMemo(() => {
    const c = { all: 0, soda: 0, maji: 0, other: 0, noprice: 0 };
    (data?.products || []).forEach((p) => { c.all++; c[typeOf(p.name)]++; if (!hasPrice(p.id)) c.noprice++; });
    return c;
  }, [data, hasPrice]);
  const visible = useMemo(() => (data?.products || []).filter((p) => {
    const term = q.trim().toLowerCase();
    if (term && !p.name.toLowerCase().includes(term)) return false;
    if (filter === "noprice") return !hasPrice(p.id);
    return filter === "all" || typeOf(p.name) === filter;
  }), [data, q, filter, hasPrice]);

  const save = async () => {
    setSaving(true); setNotice(null);
    const prices = {};
    data.packages.forEach((k) => {
      prices[k.id] = {};
      data.products.forEach((p) => { prices[k.id][p.id] = parseFloat(grid[p.id]?.[k.id]) || 0; });
    });
    try {
      const r = await api.post("/api/wholesale/prices", { prices });
      setNotice({ tone: r.success ? "ok" : "warn", text: r.message });
      load();
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setSaving(false); }
  };

  const actions = (
    <>
      <Link to="/wholesale_pos" className="btn"><i className="fas fa-arrow-left" /> Back to POS</Link>
      <button type="button" className="btn primary" onClick={save} disabled={!data || saving}>
        <i className="fas fa-check" /> {saving ? "Saving…" : "Save all prices"}
      </button>
    </>
  );

  return (
    <main className="pg" style={{ maxWidth: "96rem" }}>
      <PageHeader slug="wholesale_prices" actions={actions} />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      {data && (
        <div className="ui-stats">
          <Stat icon="fa-box" label="Products" value={counts.all} />
          <Stat icon="fa-layer-group" tone="muted" label="Package types" value={data.packages.length} />
          <Stat icon="fa-circle-check" tone="up" label="Priced in at least one package" value={counts.all - counts.noprice} />
          {counts.noprice > 0 && <Stat icon="fa-triangle-exclamation" tone="warn" label="Missing prices" value={counts.noprice} />}
        </div>
      )}

      <div className="ui-notice warn" style={{ display: "block", fontWeight: 400 }}>
        Enter the <b>total package price</b> for each product. Price per bottle = package price ÷ capacity, and that is the
        unit price sent to <code>ProcessSale()</code>. Profit per bottle = price per bottle − purchase price. Clear a cell (or enter 0)
        to remove that product from that package. Only products with a wholesale price of at least 1 are listed.
      </div>

      <section className="panel">
        <div className="toolbar">
          <div className="field grow">
            <div className="search">
              <i className="fas fa-magnifying-glass" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" aria-label="Search products" />
            </div>
          </div>
          <div className="seg-filter" role="group" aria-label="Filter products">
            {FILTERS.map(([k, label]) => (
              <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {label} <span className="mono">{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl wp-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Purchase price</th>
                {data?.packages.map((k) => (
                  <th key={k.id} colSpan={2} className="wp-pkg">{k.name}<small>{k.cap} bottles · total package price · profit per bottle</small></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const priced = hasPrice(p.id);
                return (
                  <tr key={p.id} className={priced ? "" : "wp-noprice"}>
                    <td>
                      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{p.name}</div>
                      {p.manufacturer && <div style={{ fontSize: ".75rem", color: "var(--muted)" }}>{p.manufacturer}</div>}
                      <span className="pill-tag" style={{ marginTop: ".25rem", textTransform: "capitalize" }}>{typeOf(p.name)}</span>
                      {!priced && <span className="pill-tag warn" style={{ marginLeft: ".3rem" }}>No price set</span>}
                    </td>
                    <td className="num mono">{n2(p.purchase_price)}</td>
                    {data.packages.map((k) => {
                      const v = grid[p.id]?.[k.id] ?? "";
                      const pkgPrice = parseFloat(v) || 0;
                      const ppb = pkgPrice > 0 && k.cap > 0 ? pkgPrice / k.cap : 0;
                      const profit = ppb > 0 ? ppb - p.purchase_price : null;
                      const margin = ppb > 0 && p.purchase_price > 0 ? ((ppb - p.purchase_price) / ppb) * 100 : null;
                      return [
                        <td key={`${k.id}-in`}>
                          <input className="cell" type="number" step="0.01" min="0" placeholder="0.00" value={v}
                            aria-label={`${p.name}, ${k.name} package price`}
                            onChange={(e) => setGrid((g) => ({ ...g, [p.id]: { ...g[p.id], [k.id]: e.target.value } }))} />
                        </td>,
                        <td key={`${k.id}-pf`} className="num" style={{ whiteSpace: "nowrap" }}>
                          {profit === null ? <span style={{ color: "#c7c4bc" }}>–</span> : (
                            <>
                              <span className="mono" style={{ color: profit >= 0 ? "var(--up)" : "var(--danger)", fontWeight: 500 }}>
                                {profit >= 0 ? "+" : "−"}{n2(Math.abs(profit))}
                              </span>
                              {margin !== null && <div style={{ fontSize: ".72rem", color: "var(--muted)" }}>{margin.toFixed(1)}% margin</div>}
                            </>
                          )}
                        </td>,
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && visible.length === 0 && <div className="empty"><i className="fas fa-magnifying-glass" />No products match your search or filter.</div>}
          {!data && !notice && <div className="empty"><i className="fas fa-spinner fa-spin" />Loading prices…</div>}
        </div>
      </section>
    </main>
  );
}
